import React, { useEffect } from 'react';
import {
  AttachmentPreview,
  Button,
  Layout,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
} from '@axiom/ui';

export type ChatAttachmentListModalProps = {
  fileName: string;
  /** Absent only if the upload has not answered, which is also when there is nothing to show. */
  href: string | null;
  name: string;
  onClose: () => void;
  previewUrl: string;
};

/**
 * A sent picture over the conversation, named in the header and offered in the footer. ONLY
 * concerned with showing one file - it neither fetches nor decides which attachments are worth
 * showing.
 */
export const ChatAttachmentListModal = ({
  fileName,
  href,
  name,
  onClose,
  previewUrl,
}: ChatAttachmentListModalProps) => {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  return (
    <Modal name={name} size="large">
      <ModalHeader
        name={`${name}_HEADER`}
        onClose={() => {
          onClose();
        }}
      >
        {fileName}
      </ModalHeader>
      <ModalSection name={`${name}_BODY`}>
        <Layout position="center">
          <AttachmentPreview
            fileName={fileName}
            name={`${name}_IMAGE`}
            size="auto"
            src={previewUrl}
          />
        </Layout>
      </ModalSection>
      <ModalFooter name={`${name}_ACTIONS`}>
        <Button
          variation="outline"
          name={`${name}_CLOSE`}
          onClick={() => {
            onClose();
          }}
        >
          Close
        </Button>
        {href ? (
          <Button
            download={fileName}
            name={`${name}_DOWNLOAD`}
            /* The anchor saves the file; this only shuts the modal behind it. */
            onClick={() => {
              onClose();
            }}
            to={href}
          >
            Download
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};
